import { Component, OnInit, inject } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { TpaConnectionDataService } from "@app/external-connections/services/tpa-connection.data.service";
import { GenericListConfig } from "@app/shared/models/GenericList/GenericList";
import { GenericListFieldType } from "@app/shared/models/GenericList/GenericListField";
import { Pages } from "@constants/pages/Pages";
import { nameof } from "@methods/jeffs-toolkit";
import { isGuid } from "@methods/uniqueMethods";
import { TabModel } from "@models/generic/TabModels/TabModel";
import { RendererConfig } from "@models/workspace/RendererConfig";
import { TabService } from "@services/tabs/TabService";
import { TranslatePipe, injectTranslation, provideTranslationScope } from "@app/shared/i18n/translation-scope";
import { scope } from "@app/external-connections/i18n/scope";
import { ToastrService } from "ngx-toastr";
import { Observable } from "rxjs";
import { ControlSummaryModel } from "../models/ControlSummaryModel";
import { TpaRequestDetailModel } from "../models/TpaRequestDetailModel";
import { CerrixLoaderComponent } from "@app/shared/components/cerrix-loader/cerrix-loader.component";
import { MenuSystemDirective } from "@app/shared/directives/menu-system.directive";
import { GenericListManagerComponent } from "@app/shared/generic-managers/generic-list-manager/generic-list-manager.component";
import { DateFormatPipe } from "@app/shared/pipes/date-format.pipe";

@Component({
    selector: "app-tpa-request-detail",
    templateUrl: "./tpa-request-detail.component.html",
    styleUrls: ["./tpa-request-detail.component.scss"],
    viewProviders: [provideTranslationScope(scope)],
    imports: [
        CerrixLoaderComponent,
        MenuSystemDirective,
        GenericListManagerComponent,
        DateFormatPipe,
        TranslatePipe,
    ],
})
export class TpaRequestDetailComponent implements OnInit {
    private route = inject(ActivatedRoute);
    private _pages = inject(Pages);
    private _ds = inject(TpaConnectionDataService);
    private _tabService = inject(TabService);
    private _toastr = inject(ToastrService);
    private _translation = injectTranslation(scope);

    tabID: string;
    tab: TabModel;
    id: string;

    tpaRequestDetailModel: TpaRequestDetailModel;

    validConfig: GenericListConfig;
    invalidConfig: GenericListConfig;

    rendererConfig: RendererConfig[] = [
        RendererConfig.createScoreConfig(
            nameof<ControlSummaryModel>((x: ControlSummaryModel): string => x.effectivenessScore),
            nameof<ControlSummaryModel>(
                (x: ControlSummaryModel): string => x.effectivenessScoreColor
            )
        ),
        RendererConfig.createScoreConfig(
            nameof<ControlSummaryModel>((x: ControlSummaryModel): string => x.latestPeriodScore),
            nameof<ControlSummaryModel>(
                (x: ControlSummaryModel): string => x.latestPeriodScoreColor
            )
        ),
    ];

    async ngOnInit(): Promise<void> {
        this.id = this.route.snapshot.params.id ? this.route.snapshot.params.id : this.tab.id;
        await this._translation.ready();
        this.setup();
    }

    setup(): void {
        const isExisting = this.id && isGuid(this.id.toString());
        if (!isExisting) {
            this._toastr.error(
                this._translation.t("detail.notFoundMessage"),
                this._translation.t("detail.notFoundTitle")
            );
            this.tab.close(false);
            return;
        }

        this.tpaRequestDetailModel = this.getModel();
    }

    getModel(): TpaRequestDetailModel {
        const modelCall: Observable<TpaRequestDetailModel> = this._ds.getrequestDetail(this.id);

        modelCall.subscribe((x: TpaRequestDetailModel): void => {
            this.tpaRequestDetailModel = JSON.parse(JSON.stringify(x));
            this.initValidControls();
            this.initInvalidControls();

            this.tab.showLoader = false;
        });
        return;
    }

    initValidControls(): void {
        this.validConfig = <GenericListConfig>{
            name: this._translation.t("detail.validControlsName"),
            data: this.tpaRequestDetailModel.validControls,
            allowAdd: false,
            allowEdit: false,
            allowDelete: false,
            idProp: nameof<ControlSummaryModel>((x: ControlSummaryModel): string => x.guid),
            isSortableByColumn: true,
            limitViewTo: 5,
            overviewRowActionsName: "",
            overviewRowActions: [
                {
                    icon: "fas fa-edit",
                    tooltip: this._translation.t("detail.openControlTooltip"),
                    clickEvent: (row: ControlSummaryModel): void => {
                        this.openControl(row);
                    },
                },
            ],
            rendererConfig: this.rendererConfig,
            editOverride: (row: any): void => {
                this.openControl(row);
            }, // only overview
            fields: [
                {
                    fieldName: nameof<ControlSummaryModel>(
                        (x: ControlSummaryModel): string => x.guid
                    ),
                    fieldType: GenericListFieldType.Text,
                    isReadonly: true,
                    hideInEdit: true,
                    hideInOverview: true,
                },
                {
                    fieldName: nameof<ControlSummaryModel>(
                        (x: ControlSummaryModel): string => x.identifier
                    ),
                    prettyName: this._translation.t("detail.controlIdentifierPrettyName"),
                    fieldType: GenericListFieldType.Text,
                    isReadonly: true,
                },
                {
                    fieldName: nameof<ControlSummaryModel>(
                        (x: ControlSummaryModel): string => x.name
                    ),
                    prettyName: this._translation.t("detail.namePrettyName"),
                    fieldType: GenericListFieldType.Text,
                    isReadonly: true,
                },
                {
                    fieldName: nameof<ControlSummaryModel>(
                        (x: ControlSummaryModel): string => x.keyControl
                    ),
                    prettyName: this._translation.t("detail.isKeyControlPrettyName"),
                    fieldType: GenericListFieldType.Text,
                    isReadonly: true,
                },
                {
                    fieldName: nameof<ControlSummaryModel>(
                        (x: ControlSummaryModel): string => x.organization
                    ),
                    prettyName: this._translation.t("detail.organizationPrettyName"),
                    fieldType: GenericListFieldType.Text,
                    isReadonly: true,
                },
                {
                    fieldName: nameof<ControlSummaryModel>(
                        (x: ControlSummaryModel): string => x.owner
                    ),
                    prettyName: this._translation.t("detail.ownerPrettyName"),
                    fieldType: GenericListFieldType.Text,
                    isReadonly: true,
                },
                {
                    fieldName: nameof<ControlSummaryModel>(
                        (x: ControlSummaryModel): string => x.effectivenessScore
                    ),
                    prettyName: this._translation.t("detail.effectivenessScorePrettyName"),
                    fieldType: GenericListFieldType.ColoredSelect,
                    selfInit: true,
                    isReadonly: true,
                },
                {
                    fieldName: nameof<ControlSummaryModel>(
                        (x: ControlSummaryModel): string => x.effectivenessScoreColor
                    ),
                    prettyName: this._translation.t("detail.effectivenessScorePrettyName"),
                    fieldType: GenericListFieldType.ColoredSelect,
                    isReadonly: true,
                    hideInOverview: true,
                },
                {
                    fieldName: nameof<ControlSummaryModel>(
                        (x: ControlSummaryModel): string => x.latestPeriodScore
                    ),
                    prettyName: this._translation.t("detail.latestPeriodScorePrettyName"),
                    fieldType: GenericListFieldType.ColoredSelect,
                    isReadonly: true,
                    selfInit: true,
                },
                {
                    fieldName: nameof<ControlSummaryModel>(
                        (x: ControlSummaryModel): string => x.latestPeriodScoreColor
                    ),
                    prettyName: this._translation.t("detail.latestPeriodScoreColorPrettyName"),
                    fieldType: GenericListFieldType.ColoredSelect,
                    isReadonly: true,
                    hideInOverview: true,
                },
            ],
        };
    }

    initInvalidControls(): void {
        this.invalidConfig = <GenericListConfig>{
            name: this._translation.t("detail.invalidControlsName"),
            data: this.tpaRequestDetailModel.invalidControls,
            allowAdd: false,
            allowEdit: false,
            allowDelete: false,
            idProp: nameof<ControlSummaryModel>((x: ControlSummaryModel): string => x.guid),
            isSortableByColumn: true,
            limitViewTo: 5,
            overviewRowActionsName: "",
            overviewRowActions: [
                {
                    icon: "fas fa-edit",
                    tooltip: this._translation.t("detail.openControlTooltip"),
                    clickEvent: (row: ControlSummaryModel): void => {
                        this.openControl(row);
                    },
                },
            ],
            rendererConfig: this.rendererConfig,
            editOverride: (row: any): void => {
                this.openControl(row);
            }, // only overview
            fields: [
                {
                    fieldName: nameof<ControlSummaryModel>(
                        (x: ControlSummaryModel): string => x.guid
                    ),
                    fieldType: GenericListFieldType.Text,
                    isReadonly: true,
                    hideInEdit: true,
                    hideInOverview: true,
                },
                {
                    fieldName: nameof<ControlSummaryModel>(
                        (x: ControlSummaryModel): string => x.identifier
                    ),
                    prettyName: this._translation.t("detail.controlIdentifierPrettyName"),
                    fieldType: GenericListFieldType.Text,
                    isReadonly: true,
                },
                {
                    fieldName: nameof<ControlSummaryModel>(
                        (x: ControlSummaryModel): string => x.name
                    ),
                    prettyName: this._translation.t("detail.namePrettyName"),
                    fieldType: GenericListFieldType.Text,
                    isReadonly: true,
                },
                {
                    fieldName: nameof<ControlSummaryModel>(
                        (x: ControlSummaryModel): string => x.keyControl
                    ),
                    prettyName: this._translation.t("detail.isKeyControlPrettyName"),
                    fieldType: GenericListFieldType.Text,
                    isReadonly: true,
                },
                {
                    fieldName: nameof<ControlSummaryModel>(
                        (x: ControlSummaryModel): string => x.organization
                    ),
                    prettyName: this._translation.t("detail.organizationPrettyName"),
                    fieldType: GenericListFieldType.Text,
                    isReadonly: true,
                },
                {
                    fieldName: nameof<ControlSummaryModel>(
                        (x: ControlSummaryModel): string => x.owner
                    ),
                    prettyName: this._translation.t("detail.ownerPrettyName"),
                    fieldType: GenericListFieldType.Text,
                    isReadonly: true,
                },
                {
                    fieldName: nameof<ControlSummaryModel>(
                        (x: ControlSummaryModel): string => x.effectivenessScore
                    ),
                    prettyName: this._translation.t("detail.effectivenessScorePrettyName"),
                    fieldType: GenericListFieldType.ColoredSelect,
                    selfInit: true,
                    isReadonly: true,
                },
                {
                    fieldName: nameof<ControlSummaryModel>(
                        (x: ControlSummaryModel): string => x.effectivenessScoreColor
                    ),
                    prettyName: this._translation.t("detail.effectivenessScorePrettyName"),
                    fieldType: GenericListFieldType.ColoredSelect,
                    isReadonly: true,
                    hideInOverview: true,
                },
                {
                    fieldName: nameof<ControlSummaryModel>(
                        (x: ControlSummaryModel): string => x.reasonFailed
                    ),
                    prettyName: this._translation.t("detail.reasonFailedPrettyName"),
                    fieldType: GenericListFieldType.Text,
                    isReadonly: true,
                },
            ],
        };
    }

    openControl(row: ControlSummaryModel): void {
        if (isGuid(row.guid)) {
            this._tabService.generateTab(this._pages.ControlDetail, row.guid, row.name);
        } else {
            this._toastr.warning(
                this._translation.t("detail.requestNotFoundMessage"),
                this._translation.t("detail.requestNotFoundTitle")
            );
        }
    }
}
